import React, { useGlobal } from 'reactn'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MedAccountSetting from 'ui/pages/med/AccountSetting'
import { useEffect, useState } from 'react'
import {
  getDepartmentList,
  getLogout,
  getPublicInformation,
  postDepartmentSettings,
  postPublicInformation,
  putDepartmentDisplayOrder,
  putPassword,
} from './fetch'
import { routePaths } from 'app/routes'
import { newDepartmentSettings } from 'app/medical/utils'
import { PublicInformation } from 'types/med'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'
import Stack from 'ui/layout/Stack'
import { ApiErrorWithMessage, NotFoundError } from 'app/client'

const AccountSetting: React.FC = () => {
  const { t } = useTranslation()
  const [user, setUser] = useGlobal('user')
  const [update] = useGlobal('updateInformation')
  const [publicInformation, setPublicInformation] =
    useState<PublicInformation>()
  const [departmentListItems, setDepartmentListItems] = useGlobal(
    'departmentListItems'
  )

  const [modal, setModal] = useState<string>('')

  const navigate = useNavigate()

  const getDepartmentListHandler = () =>
    getDepartmentList().then(setDepartmentListItems).catch(console.log)

  const getPublicInformationHandler = () =>
    getPublicInformation()
      .then(setPublicInformation)
      .catch((e) => {
        if (e instanceof NotFoundError) {
          return Promise.resolve()
        } else {
          console.log(e)
        }
      })

  const postPublicInformationHandler: (
    data: PublicInformation
  ) => Promise<void> = (data) => {
    return postPublicInformation(data)
      .then(getPublicInformationHandler)
      .catch(() =>
        setModal(t('errors.掲載情報の送信に失敗しました。再度お試しください。'))
      )
  }

  const createDepartment = () => {
    postDepartmentSettings({
      ...newDepartmentSettings,
      display_order: departmentListItems && departmentListItems.length + 1,
    })
      .then(() => {
        setTimeout(() => {
          getDepartmentListHandler().then(() => {
            if (!departmentListItems?.length) return
            const newId = departmentListItems[departmentListItems.length - 1].id
            navigate(
              `${routePaths.medical}${routePaths.setting}/department/${newId}`
            )
          })
        }, 300)
      })
      .catch(console.log)
  }

  useEffect(() => {
    getPublicInformationHandler()
    getDepartmentListHandler()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!user) return null

  return (
    <>
      <Modal handleClose={() => setModal('')} open={!!modal}>
        <ConfirmContent onSubmit={() => setModal('')}>
          <Stack>{modal}</Stack>
        </ConfirmContent>
      </Modal>
      <MedAccountSetting
        med={user}
        update={update}
        publicInformation={publicInformation}
        selectRoute={{
          route: 'setting',
          onSelect: (route) => navigate(`/medical/${route}`),
        }}
        settingSubmenu={{
          generalMenu: {
            current: false,
            onClick: () =>
              navigate(`${routePaths.medical}${routePaths.setting}`),
          },
          department: departmentListItems
            ? {
                list: departmentListItems,
                onSelect: (departmentId) =>
                  navigate(
                    `${routePaths.medical}${routePaths.setting}/department/${departmentId}`
                  ),
                onChange: (id, order) =>
                  putDepartmentDisplayOrder(id, order).then(
                    getDepartmentListHandler
                  ),
                onCreate: createDepartment,
              }
            : undefined,
        }}
        onLogoutClick={() => {
          getLogout()
            .then(() => {
              setUser(undefined)
              navigate('/medical')
            })
            .catch(() =>
              setModal(
                t('errors.ログアウトに失敗しました。再度お試しください。')
              )
            )
        }}
        onSubmitPasswordChange={(data) =>
          putPassword(data).catch((e) => {
            if (e instanceof ApiErrorWithMessage) {
              if (
                e.has({
                  type: 'validation_error',
                  key: 'old_password',
                  value: 'correct',
                })
              ) {
                return Promise.reject()
              }
            }
            setModal(
              t('errors.パスワードの更新に失敗しました。再度お試しください。')
            )
          })
        }
        onSubmitSavePublicInformation={postPublicInformationHandler}
      />
    </>
  )
}

export default AccountSetting
